import type { ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { AliasesEditor } from "@/pages/tina/festivals/components/aliases-editor";
import { KindSwitch } from "@/pages/tina/festivals/components/page/kind-switch";
import type {
  PageForm,
  PageFormAction,
} from "@/pages/tina/festivals/lib/page-form";

interface FestivalSectionProps {
  form: PageForm;
  dispatch: (action: PageFormAction) => void;
  kind: string;
  /** The name and the other names can be edited (a day-ticket page); a program page shows them as text. */
  editable: boolean;
  /** Asks for the other type; without it the type switch is not shown (a read-only page). */
  onAskKind?: (to: "day_ticket" | "program") => void;
  busy?: boolean;
  /** The suggestion of another name Tina found. */
  children?: ReactNode;
}

/** The festival's name, the other names it goes by and its type. */
export function FestivalSection({
  form,
  dispatch,
  kind,
  editable,
  onAskKind,
  busy = false,
  children,
}: FestivalSectionProps) {
  return (
    <section className="flex flex-col gap-4 rounded-lg border p-4">
      <h2 className="text-lg font-semibold">Festival</h2>
      <div className="flex flex-col gap-1">
        <label htmlFor="festival-name" className="text-sm font-medium">
          Name
        </label>
        {editable ? (
          <Input
            id="festival-name"
            className="max-w-md"
            value={form.name}
            onChange={(event) =>
              dispatch({ type: "set_name", name: event.target.value })
            }
          />
        ) : (
          <p id="festival-name" className="text-sm">
            {form.name}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium">Other names</span>
        {editable ? (
          <AliasesEditor
            aliases={form.aliases}
            knownKeys={[]}
            onChange={(aliases) => dispatch({ type: "set_aliases", aliases })}
          />
        ) : form.aliases.length > 0 ? (
          <ul className="flex flex-wrap gap-2">
            {form.aliases.map((alias) => (
              <li key={alias}>
                <Badge variant="outline" className="text-xs font-normal">
                  {alias}
                </Badge>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">None</p>
        )}
      </div>
      {children}
      {onAskKind ? (
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium">Type</span>
          <KindSwitch kind={kind} disabled={busy} onAsk={onAskKind} />
        </div>
      ) : null}
    </section>
  );
}
